import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { OnboardingProps } from "pages/onboarding/OnboardCompany";
import { getOwnerUserValidation } from "validations/ownerUser";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import useAddUpdateUser, {
  User,
  UsersQueryData,
  userFromNode,
} from "hooks/useAddUpdateUser";
import {
  GET_ONBOARDING_OWNER_RULES,
  GET_USERS,
  REMOVE_ONBOARDING_OWNER,
  SAVE_ONBOARDING_OWNER,
} from "graphql/user";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { toast } from "@outpave/ds";
import UserForm from "./UserForm";
import OwnersList from "./OwnersList";
import StepHeader from "./StepHeader";
import StepFooter from "./StepFooter";
import { HugeiconsIcon } from "@hugeicons/react";
import { AddCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import dayjs from "utils/dayjs";
import {
  isSameProfile,
  toOnboardingOwnerInput,
} from "utils/onboarding/onboardingProfiles";

const MAX_OWNERS = 4;

interface OnboardingOwnerRulesData {
  getOnboardingOwnerRules: { wholeOwnershipPercentage: boolean };
}

const Owners: React.FC<OnboardingProps> = (props) => {
  const { t } = useTranslation();
  const { onboardingInfo, handleNext, handleBack, handleSetOnboardingInfo } =
    props;
  const { connectionId } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [owners, setOwners] = React.useState<User[]>([]);
  const [selectedOwner, setSelectedOwner] = React.useState<User | null>(null);
  const [showOwnerForm, setShowOwnerForm] = React.useState(false);
  const [resetForm, setResetForm] = React.useState(false);

  const {
    data: usersData,
    loading: userLoading,
    refetch: refetchOwners,
  } = useQuery<UsersQueryData>(GET_USERS, {
    variables: {
      isOwner: true,
      includeVerificationOnly: true,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!usersData) return;
    const fetchedOwners = usersData.users.edges.map((edge) =>
      userFromNode(edge.node),
    );
    // A profile edited in this session wins over the stored owner it matches.
    const updatedOwners = fetchedOwners.map((owner) => {
      const profile = onboardingInfo?.userProfiles?.find((u) =>
        isSameProfile(u, owner),
      );
      return profile
        ? {
            ...profile,
            id: owner.id,
            access: owner.access,
          }
        : {
            ...owner,
            birthdate: dayjs(owner?.birthdate).format("YYYY-MM-DD"),
          };
    });
    setOwners(updatedOwners);
    updateOwners(updatedOwners);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersData]);

  const {
    data: rulesData,
    loading: rulesLoading,
    refetch: refetchRules,
  } = useQuery<OnboardingOwnerRulesData>(GET_ONBOARDING_OWNER_RULES, {
    variables: { connectionId },
    notifyOnNetworkStatusChange: true,
  });
  // Unknown until the issuer answers, which holds Save, Next and Add.
  const wholeOwnershipPercentage =
    rulesData?.getOnboardingOwnerRules.wholeOwnershipPercentage;
  const rulesKnown = wholeOwnershipPercentage !== undefined;
  const ownerUserValidation = useMemo(
    () =>
      getOwnerUserValidation({
        wholeOwnershipPercentage: Boolean(wholeOwnershipPercentage),
      }),
    [wholeOwnershipPercentage],
  );

  const [saveOwner, { loading: savingOwner }] = useMutation(
    SAVE_ONBOARDING_OWNER,
  );
  const [removeOwner, { loading: removingOwner }] = useMutation(
    REMOVE_ONBOARDING_OWNER,
  );

  const [findUsersByEmail, { loading: lookingUpEmail }] =
    useLazyQuery<UsersQueryData>(GET_USERS, { fetchPolicy: "network-only" });

  const {
    formik: ownerUserProfile,
    loading: userProfileLoading,
    setUser,
  } = useAddUpdateUser({
    initialValues: {
      identifyingType: "SSN",
      issuingCountry: "US",
      isOwner: true,
      location: { id: onboardingInfo?.addressProfile?.id },
    },
    staticValues: {
      isOwner: true,
    },
    id: selectedOwner?.id,
    validationSchema: ownerUserValidation,
  });

  useEffect(() => {
    setLoading(
      userLoading ||
        rulesLoading ||
        userProfileLoading ||
        savingOwner ||
        removingOwner,
    );
  }, [
    userLoading,
    rulesLoading,
    userProfileLoading,
    savingOwner,
    removingOwner,
  ]);

  useEffect(() => {
    if (resetForm) {
      ownerUserProfile.resetForm();
      setResetForm(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetForm]);

  // Someone already on the company is adopted, so saving updates them rather
  // than failing as a duplicate. Save stays disabled while this runs.
  async function adoptExistingUser(email: string) {
    const normalized = email.trim().toLowerCase();
    if (ownerUserProfile.values.id || !normalized) return;
    const { data } = await findUsersByEmail({
      variables: { email: normalized, includeVerificationOnly: true },
    });
    const existing = data?.users.edges
      .map((edge) => userFromNode(edge.node))
      .find((user) => user.email?.toLowerCase() === normalized);
    if (existing) await setUser(existing);
  }

  function retryRules() {
    refetchRules().catch(() => undefined);
  }

  function handleAdd() {
    setSelectedOwner(null);
    setResetForm(true);
    setShowOwnerForm(true);
  }

  function handleEdit(owner: User) {
    const updatedOwners = getUserProfiles().filter((user) => user.isOwner);
    setOwners(updatedOwners);
    const cachedOwner = updatedOwners.find((o) => o.id === owner.id);
    if (cachedOwner) {
      setUser(cachedOwner);
    } else {
      setSelectedOwner(owner);
    }
    setShowOwnerForm(true);
  }

  function updateOwners(owners: User[]) {
    handleSetOnboardingInfo({
      userProfiles: owners,
    });
  }

  async function handleRemove(owner: User) {
    try {
      await removeOwner({ variables: { id: owner.id } });
      const removed = onboardingInfo?.userProfiles?.find(
        (user) => user.id === owner.id,
      );
      if (removed) updateOwners([{ ...removed, isOwner: false }]);
      await refetchOwners();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  function getUserProfiles(): User[] {
    const users = onboardingInfo?.userProfiles ?? [];
    const owner = ownerUserProfile.values;

    if (!owner.id && !owner.email) return users;

    const hasOwner = users.some((user) => user.id === owner.id);
    const allUsers = hasOwner ? users : [...users, owner];

    return allUsers.map((user) =>
      user.id === owner.id ? { ...user, ...owner } : user,
    );
  }

  async function handleUpdateOnboardingInfo() {
    updateOwners(getUserProfiles());
    await refetchOwners();
  }

  async function validateOwners(owners: User[]): Promise<boolean> {
    let allValid = true;
    const updatedOwners = await Promise.all(
      owners.map(async (owner) => {
        try {
          await ownerUserValidation.validate(owner);
          return { ...owner, isValidated: true };
        } catch (err) {
          console.error("Validation Error", err);
          allValid = false;
          return { ...owner, isValidated: false };
        }
      }),
    );
    setOwners(updatedOwners);
    return allValid;
  }

  async function validateForm(): Promise<boolean> {
    try {
      const errors = await ownerUserProfile.validateForm();
      const hasErrors = Object.keys(errors).length > 0;

      ownerUserProfile.setTouched(
        Object.keys(ownerUserProfile.values).reduce<Record<string, boolean>>(
          (acc, key) => {
            acc[key] = true;
            return acc;
          },
          {},
        ),
      );
      return !hasErrors;
    } catch (err) {
      return false;
    }
  }

  async function handleSubmit() {
    if (showOwnerForm) {
      const valid = await validateForm();
      if (!valid) return;
      const owner = ownerUserProfile.values;
      try {
        await saveOwner({
          variables: {
            ...(owner.id && { id: owner.id }),
            ...(connectionId && { connectionId }),
            input: toOnboardingOwnerInput(owner),
          },
        });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : String(err));
        return;
      }
      setSelectedOwner(null);
      setResetForm(true);
      await handleUpdateOnboardingInfo();
      setShowOwnerForm(false);
    } else {
      const validated = await validateOwners(owners);
      if (!validated) return;
      await handleUpdateOnboardingInfo();
      handleNext({});
    }
  }

  function handleBackClick() {
    if (showOwnerForm) {
      setSelectedOwner(null);
      setShowOwnerForm(false);
      setResetForm(true);
    } else {
      handleUpdateOnboardingInfo();
      handleBack({});
    }
  }

  return (
    <>
      <StepHeader
        title={t("Beneficial owners")}
        subtitle={t("BeneficialOwnersDescription")}
      />

      <Box sx={{ marginTop: "var(--space-7)" }}>
        {!rulesKnown && !rulesLoading && (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button variant="default-outlined" onClick={retryRules}>
                {t("Retry")}
              </Button>
            }
            sx={{ marginBottom: "var(--space-5)" }}
          >
            {t("OwnerRulesLoadError")}
          </Alert>
        )}
        {showOwnerForm ? (
          <UserForm
            formik={ownerUserProfile}
            showEmailField={true}
            onEmailBlur={adoptExistingUser}
            showOwnership={true}
            wholeOwnershipPercentage={wholeOwnershipPercentage}
            showInviteChoice={!ownerUserProfile.values.id}
          />
        ) : (
          <Stack gap="var(--space-5)">
            {owners.length > 0 ? (
              <OwnersList
                owners={owners}
                onEdit={handleEdit}
                onRemove={handleRemove}
              />
            ) : (
              <Box
                sx={{
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 4,
                  textAlign: "center",
                }}
              >
                <Typography variant="subtitle1">
                  {t("No beneficial owners added yet")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("BeneficialOwnersEmptyHint")}
                </Typography>
              </Box>
            )}
            {owners.length < MAX_OWNERS && (
              <Button
                variant="default-outlined"
                onClick={handleAdd}
                disabled={!rulesKnown}
                sx={{ alignSelf: "flex-start" }}
                startIcon={
                  <HugeiconsIcon
                    icon={AddCircleIcon}
                    size={20}
                    color="currentColor"
                  />
                }
              >
                {t("Add a beneficial owner")}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      <StepFooter
        primaryLabel={showOwnerForm ? t("Save") : t("Next")}
        onPrimary={handleSubmit}
        primaryDisabled={
          (!showOwnerForm && owners.length === 0) ||
          lookingUpEmail ||
          !rulesKnown
        }
        secondaryLabel={showOwnerForm ? t("Cancel") : t("Previous")}
        onSecondary={handleBackClick}
      />
      <LoadingWithoutModal open={loading} />
    </>
  );
};

export default Owners;
